/**
 * MIC-3 — propose a new material when search finds no match.
 * Does not insert into mfg_materials until admin approves.
 */
import { useState } from "react";
import {
  inputClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  suggestedName: string;
  disabled: boolean;
  busy: boolean;
  onSubmit: (opts: {
    proposed_name: string;
    proposed_unit: string;
    proposed_category: string;
    proposed_qty: number | null;
  }) => void;
  onCancel: () => void;
};

export function ManufacturingInventoryCountNewItemComposer({
  suggestedName,
  disabled,
  busy,
  onSubmit,
  onCancel,
}: Props) {
  const [name, setName] = useState(suggestedName);
  const [unit, setUnit] = useState("each");
  const [category, setCategory] = useState("general");
  const [qty, setQty] = useState("");

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-900 dark:bg-amber-950/30">
      <p className="text-sm font-medium text-amber-950 dark:text-amber-100">Propose new item</p>
      <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-200/80">
        Does not create a live SKU until an admin approves. Prevents rushed duplicates when packaging
        or verbiage changed.
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <label className="block text-xs text-gray-700 dark:text-zinc-300 sm:col-span-2">
          Name
          <input
            className={`${inputClass} mt-1`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disabled || busy}
          />
        </label>
        <label className="block text-xs text-gray-700 dark:text-zinc-300">
          Unit
          <input
            className={`${inputClass} mt-1`}
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={disabled || busy}
          />
        </label>
        <label className="block text-xs text-gray-700 dark:text-zinc-300">
          Category
          <input
            className={`${inputClass} mt-1`}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={disabled || busy}
          />
        </label>
        <label className="block text-xs text-gray-700 dark:text-zinc-300 sm:col-span-2">
          Counted qty (optional)
          <input
            className={`${inputClass} mt-1`}
            inputMode="decimal"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            disabled={disabled || busy}
            placeholder="Applied when approved"
          />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={disabled || busy || !name.trim()}
          onClick={() => {
            const trimmedQty = qty.trim();
            const proposed_qty =
              trimmedQty === ""
                ? null
                : Number.isFinite(Number(trimmedQty)) && Number(trimmedQty) >= 0
                  ? Number(trimmedQty)
                  : null;
            onSubmit({
              proposed_name: name.trim(),
              proposed_unit: unit.trim() || "each",
              proposed_category: category.trim() || "general",
              proposed_qty,
            });
          }}
        >
          {busy ? "Submitting…" : "Submit proposal"}
        </button>
        <button type="button" className={mfgSecondaryBtnClass} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
